import React from 'react'

import { OmForm, OmFormItem, OmInput, OmButton } from '@owma/kit'

import classNames from 'classnames'
import i18n from 'i18n'

import { Provider } from 'react-redux'
import { store } from 'index'

import LanguageSwitcher from 'components/cleanui/layout/TopBar/LanguageSwitcher'

import { checkOnline } from 'utils'
import { confirmBase, checkOpenedWindow, ModalInstance } from 'utils/ui'
import iconClose20SVG from 'assets/images/icon-close-20.svg'
import EmailForm from 'pages/main/components/EmailForm'
import { POPUP_EMAIL_EDITOR_PATH } from 'services/popup/isPopupMode'
import {
  registerPopupSendHelper,
  unregisterPopupSendHelper,
  POPUP_WIN_ID_PARAM,
} from 'pages/popup/handoff'

import SelectUser from 'popups/component/SelectUser'
import ScheduleSend from 'popups/component/ScheduleSend'
import EMLPreview from 'popups/component/EMLPreview'
import Logo from 'components/cleanui/layout/TopBar/Logo'
import logo128SVG from 'assets/images/logo/logo-128.svg'

import backIcon from 'assets/images/toolbar-back.svg'
import syncAniGIF from 'assets/images/gif/loading.gif'
import LanguageIcon from 'assets/images/menu-language.svg'

import styles from './style.module.scss'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

// dayjs 플러그인 등록
dayjs.extend(utc)
dayjs.extend(timezone)

interface SelectUserOptions {
  userOnly?: boolean
  defaultEmailList?: string[]
  excludeEmailList?: string[]
  disableEmails?: string[]
  appendUserTitles?: boolean
  title?: string
  okText?: string
  onUserSelectChanged?: (selectedList: string[], selectedUserList: any[]) => void
  onOk?: (
    result: { selectedEmailList: string[]; selectedUserList: any[] },
    closeFunc: () => void,
  ) => void
  onCancel?: (reason: string) => void
  afterClose?: () => void
}

export function selectUser(opts0?: SelectUserOptions): any {
  const opts: SelectUserOptions = opts0 || {}

  const extObj: any = {
    compAttrs: {
      userOnly: opts.userOnly,
      defaultEmailList: opts.defaultEmailList,
      excludeEmailList: opts.excludeEmailList,
      disableEmails: opts.disableEmails,
      appendUserTitles: opts.appendUserTitles,
    },
    selectedList: [],
    selectedUserList: [],
  }

  extObj.compAttrs.onUserSelectChanged = (
    changedEmailList: string[],
    changedUserList: any[],
  ): void => {
    extObj.selectedList = [...changedEmailList]
    extObj.selectedUserList = [...changedUserList]

    extObj.updateModal()

    if (opts.onUserSelectChanged != null) {
      opts.onUserSelectChanged(extObj.selectedList, extObj.selectedUserList)
    }
  }

  extObj.close = (): void => {
    if (opts.onCancel != null) {
      opts.onCancel('cancel')
    }
    extObj.modalObj.destroy()
  }

  const titleElem = (
    <React.Fragment>
      <OmButton
        variant="text"
        icon={<img src={backIcon} alt="Back" />}
        className={classNames(styles.backButton, 'backButton')}
        onClick={extObj.close}
      />
      <span>{opts.title}</span>
    </React.Fragment>
  )

  const modalCfg: any = {
    title: titleElem,
    wrapClassName: classNames(styles.selectUserPopup),
    className: classNames('mobile-fullpage'),
    icon: null,
    content: <SelectUser {...extObj.compAttrs} />,
    okText: opts.okText || i18n.t('common.button.ok'),
    cancelText: i18n.t('common.button.cancel'),
    maskTransitionName: 'maskTransitionName',
    width: 480,
    maskClosable: true,
    centered: true,
    okButtonProps: {
      disabled: extObj.selectedList.length < 1,
      className: classNames('type1', 'h38'),
      style: { minWidth: '100px' },
    },
    cancelButtonProps: {
      className: classNames('h38'),
      style: { minWidth: '100px' },
    },
    onOk: (closeFunc: () => void): void => {
      if (opts.onOk != null) {
        opts.onOk(
          {
            selectedEmailList: extObj.selectedList,
            selectedUserList: extObj.selectedUserList,
          },
          closeFunc,
        )
      } else {
        closeFunc()
      }
    },
    onCancel: (): void => {
      if (opts.onCancel != null) {
        opts.onCancel('cancel')
      }
    },
    afterClose: (): void => {
      if (opts.afterClose != null) {
        opts.afterClose()
      }
    },
  }

  extObj.modalObj = confirmBase(modalCfg)

  extObj.updateModal = (): void => {
    extObj.modalObj.updateBase({
      okButtonProps: {
        disabled: extObj.selectedList.length < 1,
        className: classNames('type1', 'h38'),
        style: { minWidth: '100px' },
      },
    })
  }

  return extObj
}

interface LoadingPopupOptions {
  render?: () => React.ReactElement
  okText?: string
  onCancel?: (reason: string) => void
  afterClose?: () => void
  timeout?: number
  wrapClassName?: string
}

export function showLoadingPopup(opts0?: LoadingPopupOptions): any {
  const opts: LoadingPopupOptions = opts0 || {}

  const extObj: any = {}

  extObj.close = (): void => {
    if (opts.onCancel != null) {
      opts.onCancel('cancel')
    }
    extObj.modalObj.destroy()
  }

  extObj.render = (): React.ReactElement => {
    let content = null
    if (opts.render != null) {
      content = <div className={classNames(styles.content)}>{opts.render()}</div>
    }
    return (
      <React.Fragment>
        <div className={classNames(styles.loadingAni)}>
          <img src={syncAniGIF} alt="sync" />
        </div>
        {content}
      </React.Fragment>
    )
  }

  const modalCfg: any = {
    title: null,
    wrapClassName: classNames(styles.showLoadingPopup),
    className: null,
    icon: null,
    content: extObj.render(),
    okText: opts.okText || i18n.t('common.button.ok'),
    cancelText: i18n.t('common.button.cancel'),
    maskTransitionName: 'maskTransitionName',
    width: 380,
    maskClosable: false,
    centered: true,
    footer: null,
    afterClose: (): void => {
      if (opts.afterClose != null) {
        opts.afterClose()
      }
    },
  }

  extObj.modalObj = confirmBase(modalCfg)

  extObj.updateModal = (): void => {
    extObj.modalObj.updateBase({
      content: extObj.render(),
    })
  }

  if (opts.timeout != null && opts.timeout > 0) {
    setTimeout(() => {
      extObj.close()
    }, opts.timeout)
  }

  return extObj
}

interface ScheduleSendOptions {
  title?: string
  initDateTime?: Date | Dayjs | null
  onPreviewEML?: () => void
  onOk?: (selectedDateTime: Dayjs | null, closeFunc: () => void) => void
  onCancel?: (reason: string) => void
  afterClose?: () => void
}

export function scheduleSend(opts0?: ScheduleSendOptions): any {
  const opts: ScheduleSendOptions = opts0 || {}

  const isNew = opts.initDateTime == null

  const selectedDateTime =
    opts.initDateTime != null
      ? dayjs(opts.initDateTime)
      : dayjs().add(1, 'day').hour(9).minute(0).second(0)

  console.log(' ---- selectedDateTime : ', selectedDateTime)

  const extObj: any = {
    selectedDateTime,
    isValid: true,
  }

  extObj.onDateTimeChange = (dateTime: Dayjs | null, isValid: boolean): void => {
    extObj.selectedDateTime = dateTime
    extObj.isValid = isValid
    extObj.updateModal()
  }

  extObj.close = (): void => {
    if (opts.onCancel != null) {
      opts.onCancel('cancel')
    }
    extObj.modalObj.destroy()
  }

  const modalCfg: any = {
    title: i18n.t('scheduleSend.title'),
    wrapClassName: 'om-popup-base',
    icon: null,
    content: (
      <ScheduleSend
        initDateTime={extObj.selectedDateTime}
        onDateTimeChange={extObj.onDateTimeChange}
        onPreviewEML={opts.onPreviewEML}
      />
    ),
    okText: isNew === true ? i18n.t('common.button.send') : i18n.t('common.button.save'),
    cancelText: i18n.t('common.button.cancel'),
    width: 480,
    maskClosable: true,
    centered: true,
    okButtonProps: {
      className: classNames('type1', 'h38'),
      style: { minWidth: '100px' },
    },
    cancelButtonProps: {
      className: classNames('h38'),
      style: { minWidth: '100px' },
    },
    onOk: (closeFunc: () => void): void => {
      if (!extObj.isValid || !extObj.selectedDateTime) {
        confirmBase({
          icon: <img src={iconClose20SVG} alt="error" />,
          title: 'Invalid Time',
          content: 'Please select a future date and time.',
          okText: 'OK',
          cancelText: null,
          width: 380,
        })
        return
      }

      if (opts.onOk != null) {
        opts.onOk(extObj.selectedDateTime, closeFunc)
      } else {
        closeFunc()
      }
    },
    onCancel: (): void => {
      if (opts.onCancel != null) {
        opts.onCancel('cancel')
      }
    },
    afterClose: (): void => {
      if (opts.afterClose != null) {
        opts.afterClose()
      }
    },
  }

  extObj.modalObj = confirmBase(modalCfg)

  extObj.updateModal = (): void => {
    extObj.modalObj.updateBase({
      okButtonProps: {
        disabled: !extObj.isValid,
        className: classNames('type1', 'h38'),
        style: { minWidth: '100px' },
      },
    })
  }

  return extObj
}

interface EMLPreviewOptions {
  title?: string
  emlContent?: string
  emailData?: any
  validationResult?: any
  okText?: string
  onOk?: (closeFunc: () => void) => void
  onCancel?: (reason: string) => void
  afterClose?: () => void
}

export function emlPreview(opts0?: EMLPreviewOptions): any {
  const opts: EMLPreviewOptions = opts0 || {}

  const extObj: any = {}

  extObj.close = (): void => {
    if (opts.onCancel != null) {
      opts.onCancel('cancel')
    }
    extObj.modalObj.destroy()
  }

  const modalCfg: any = {
    title: opts.title || 'EML Preview',
    wrapClassName: 'om-popup-base',
    icon: null,
    content: (
      <EMLPreview
        emlContent={opts.emlContent as any}
        emailData={opts.emailData}
        validationResult={opts.validationResult}
        onClose={extObj.close as any}
      />
    ),
    okText: opts.okText || i18n.t('common.button.ok'),
    cancelText: i18n.t('common.button.cancel'),
    width: 800,
    maskClosable: true,
    centered: true,
    okButtonProps: {
      className: classNames('type1', 'h38'),
      style: { minWidth: '100px' },
    },
    cancelButtonProps: {
      className: classNames('h38'),
      style: { minWidth: '100px' },
    },
    onOk: (closeFunc: () => void): void => {
      if (opts.onOk != null) {
        opts.onOk(closeFunc)
      } else {
        closeFunc()
      }
    },
    onCancel: (): void => {
      if (opts.onCancel != null) {
        opts.onCancel('cancel')
      }
    },
    afterClose: (): void => {
      if (opts.afterClose != null) {
        opts.afterClose()
      }
    },
  }

  extObj.modalObj = confirmBase(modalCfg)

  return extObj
}

interface SendMailInnerPopupOptions {
  title?: string
}

export function openSendMailInnerPopup(sendHelper: any, options?: SendMailInnerPopupOptions): any {
  const { title } = options || {}

  const tmpObj: any = {
    title: title || i18n.t('email.title.newEmail'),
    onSended: (): void => {
      tmpObj.modalObj.destroy()
    },
    onCanceled: (): void => {
      tmpObj.modalObj.destroy()
    },
  }

  tmpObj.render = (): React.ReactElement => {
    return (
      <Provider store={store}>
        <EmailForm helper={sendHelper} onSended={tmpObj.onSended} onCanceled={tmpObj.onCanceled} />
      </Provider>
    )
  }

  tmpObj.modalObj = confirmBase({
    wrapClassName: classNames('noPadding', 'noBtn', 'email-editor'),
    title: tmpObj.title,
    content: tmpObj.render(),
    onCancel: (): void => {
      if (sendHelper.formAPIs != null && sendHelper.formAPIs.callCancel != null) {
        sendHelper.formAPIs.callCancel()
      }
    },
    footer: null,
  })

  tmpObj.updateModal = (): void => {
    tmpObj.modalObj.update()
  }

  return tmpObj
}

interface SendMailWindowOptions {
  title?: string
  // true 면 매 호출마다 독립된 새 창을 연다(여러 편집 창 동시 사용).
  // false(기본)면 고정 window name 을 재사용해 단일 창만 유지한다(기존 동작).
  allowMultiple?: boolean
}

export function openSendMailWindow(sendHelper: any, opts?: SendMailWindowOptions): void {
  const { title, allowMultiple } = opts || {}

  const windowSize = {
    width: Math.min(window.screen.availWidth, 1024),
    height: Math.min(window.screen.availHeight, 711),
  }
  const windowLocation = {
    left:
      ((window.screen as any).availLeft || 0) + window.screen.availWidth / 2 - windowSize.width / 2,
    top:
      ((window.screen as any).availTop || 0) +
      window.screen.availHeight / 2 -
      windowSize.height / 2,
  }

  const optionProps = {
    width: Math.round(windowSize.width),
    height: Math.round(windowSize.height),
    left: Math.round(windowLocation.left),
    top: Math.round(windowLocation.top),
    location: 'no',
    status: 'no',
    scrollbars: 'yes',
  }

  const optionList = Object.entries(optionProps).map(([key, value]) => {
    return `${key}=${value}`
  })

  const options = optionList.join(',')

  const winTitle = title || i18n.t('email.title.newEmail')

  // native window(popup)는 /popup/email-editor 에서 자체 React 앱으로 부트스트랩된다.
  // 이미 구성된 sendHelper(new/reply/forward 컨텍스트 포함)는 opener 레지스트리로 넘기고,
  // popup(EmailEditorWindow)이 winId 로 꺼내 렌더한다. (기존 iframe handoff 방식을 대체)
  const winId = registerPopupSendHelper(sendHelper, winTitle)
  const url = `${POPUP_EMAIL_EDITOR_PATH}?${POPUP_WIN_ID_PARAM}=${encodeURIComponent(winId)}`

  // allowMultiple 이면 winId 기반 고유 window name 으로 매번 새 창을 연다.
  // 아니면 고정 name 을 써서 기존처럼 단일 창을 재사용한다(같은 name 재호출 시 기존 창 대체).
  const winName = allowMultiple === true ? `newEmailWin-${winId}` : `newEmailWin`

  const popup = window.open(url, winName, options)
  if (popup == null) {
    // 팝업 차단: popup 이 winId 를 소비하지 못하므로 등록된 handoff 항목(SendHelper+prevEmail)을
    // 즉시 제거해 누수를 막는다.
    unregisterPopupSendHelper(winId)
    return
  }
  // null 이 아닌 창만 추적 목록에 넣는다(널 push 방지).
  checkOpenedWindow(popup)
  // beforeunload(초안 저장 확인)·notification 정리는 popup 자체(EmailEditorWindow)가 처리한다.
  popup.focus()
}

interface ConfirmUIOptions {
  initialText?: string
  rules?: any[]
  isValidate?: (text: string) => boolean
  desc?: string
  label?: string
  placeholder?: string
  title?: string
  wrapClassName?: string
  width?: number
  okText?: string
  cancelText?: string
  onOk?: (text: string, closeFunc: () => void) => void
}

export function openConfirmUI(options?: ConfirmUIOptions): void {
  const opts: ConfirmUIOptions = options || {}

  const tmpObj: any = {
    formRef: React.createRef(),
    btnDisabled: true,
    ref: null,
    rules: opts.rules || [{ required: true, message: '' }],
  }

  if (opts.initialText != null) {
    tmpObj.initialValues = { text: opts.initialText }
  }

  tmpObj.onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    console.log(e)
    const text = e.target.value
    if (text.trim().length > 0) {
      if (opts.isValidate == null || opts.isValidate(text) === true) {
        tmpObj.btnDisabled = false
      } else {
        tmpObj.btnDisabled = true
      }
    } else {
      tmpObj.btnDisabled = true
    }

    tmpObj.text = text

    tmpObj.updateModal()
  }

  tmpObj.onOk = (closeFunc: () => void): void => {
    if (opts.onOk != null) {
      opts.onOk(tmpObj.text, closeFunc)
    } else {
      closeFunc()
    }
  }

  tmpObj.onPressEnter = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    console.log(' --- onPressEnter : ', e)
    if (tmpObj.btnDisabled !== true) {
      tmpObj.onOk(() => {
        tmpObj.modalObj.destroy()
      })
    }
  }

  tmpObj.setRef = (ref: HTMLInputElement | null): void => {
    if (tmpObj.ref == null && tmpObj.ref !== ref) {
      tmpObj.ref = ref
      console.log(' --- setRef : ', ref)
      setTimeout(() => {
        tmpObj.ref.select()
      }, 100)
    }
  }

  tmpObj.render = (): React.ReactElement => {
    const formAttrs: any = {
      className: classNames('line-form'),
      ref: tmpObj.formRef,
      requiredMark: false,
      layout: 'vertical' as const,
    }

    if (tmpObj.initialValues != null) {
      formAttrs.initialValues = tmpObj.initialValues
    }

    const descElem =
      opts.desc != null ? <div className={classNames(styles.desc)}>{opts.desc}</div> : null

    return (
      <React.Fragment>
        {descElem}
        <OmForm {...formAttrs}>
          <OmFormItem name="text" label={opts.label} rules={tmpObj.rules}>
            <OmInput
              ref={tmpObj.setRef}
              autoFocus={true}
              placeholder={opts.placeholder}
              onChangeEvent={tmpObj.onChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') tmpObj.onPressEnter(e)
              }}
            />
          </OmFormItem>
        </OmForm>
      </React.Fragment>
    )
  }

  tmpObj.modalObj = confirmBase({
    wrapClassName: classNames(styles.confirmUI, opts.wrapClassName),
    closable: false,
    title: opts.title,
    content: tmpObj.render(),
    width: opts.width || 360,
    okText: opts.okText || i18n.t('common.button.ok'),
    cancelText: opts.cancelText || i18n.t('common.button.cancel'),
    okButtonProps: { disabled: tmpObj.btnDisabled },
    onOk: (closeFunc: () => void): void => {
      tmpObj.onOk(closeFunc)
    },
  })

  tmpObj.updateModal = (): void => {
    tmpObj.modalObj.updateBase({
      okButtonProps: { disabled: tmpObj.btnDisabled },
    })
  }
}

interface OfflineUIOptions {
  onClickRetry?: () => void
  autoReload?: boolean
  wrapClassName?: string
}

interface OfflineUIResult {
  onClickRetry: () => void
  onOnline: () => void
  modalObj: ModalInstance
  render: () => React.ReactElement
  updateModal: () => void
}

let activeOfflineUI: OfflineUIResult | null = null

/**
 * 네트워크 오프라인 상태를 감지하여 오프라인 UI를 표시한다.
 *
 * [구현 기준]
 * - 오프라인 UI는 싱글톤으로 동작한다. 이미 표시 중이면 기존 인스턴스를 반환한다.
 * - 오프라인 판정은 2단계로 수행한다:
 *   1단계: navigator.onLine === true 이면 즉시 온라인으로 판정한다.
 *   2단계: navigator.onLine === false 이면 checkOnline()으로 실제 서버 fetch를 수행하여 재검증한다.
 * - online 이벤트 발생 시 오프라인 UI를 해제한다.
 *
 * [가정]
 * - navigator.onLine === true 는 신뢰할 수 있다. (온라인 상태를 정확히 반영)
 * - navigator.onLine === false 는 신뢰할 수 없다. (실제로는 온라인일 수 있어 재검증 필요)
 * - online/offline 이벤트는 정상적으로 발생한다.
 * - 서버 다운(네트워크는 정상)과 네트워크 오프라인은 별개이며, 이 함수는 네트워크 오프라인만 처리한다.
 *
 * [가정이 틀린 경우 대응]
 * - navigator.onLine === true 가 부정확한 경우:
 *   → 1단계에서 온라인으로 오판하여 오프라인 UI가 표시되지 않음.
 *   → 1단계 조건을 제거하고 항상 checkOnline()으로 검증하도록 변경.
 * - online/offline 이벤트가 발생하지 않는 경우:
 *   → checkOnline()의 캐시값이 갱신되지 않아 2단계 판정이 부정확해짐.
 *   → checkOnline()에 캐시 무효화(fresh) 옵션을 추가하여 매번 실제 fetch를 수행하도록 변경.
 *   → 오프라인 UI가 해제되지 않음. Retry 버튼(페이지 reload)으로만 복구 가능.
 */
export async function checkOffline(options?: OfflineUIOptions): Promise<OfflineUIResult | null> {
  // 1단계: navigator.onLine === true 는 신뢰할 수 있으므로 즉시 온라인 판정.
  if (window.navigator.onLine === true) {
    return null
  }

  // 2단계: navigator.onLine === false 는 신뢰할 수 없으므로 실제 서버 fetch로 재검증.
  const isOnline = await checkOnline()
  if (isOnline) {
    return null
  }

  // 이미 실행된 offline팝업이 있으면 그대로 사용.
  if (activeOfflineUI != null) {
    return activeOfflineUI
  }

  const opts: OfflineUIOptions = options || {}

  const reload = (): void => {
    window.location.reload()
  }

  const result: Partial<OfflineUIResult> = {
    onClickRetry: opts.onClickRetry || reload,
  }

  result.onOnline = (): void => {
    if (opts.autoReload === true) {
      reload()
    } else {
      result.modalObj?.destroy()
      window.removeEventListener('online', result.onOnline!)
    }
    activeOfflineUI = null
  }

  window.addEventListener('online', result.onOnline)

  result.render = (): React.ReactElement => {
    return (
      <Provider store={store}>
        <div className={classNames(styles.content)}>
          <div className={classNames(styles.logo)}>
            <img src={logo128SVG} alt="logo" />
            <Logo alt="officemail" noWrapper={undefined} className={undefined} />
          </div>
          <div className={classNames(styles.offlineImg)}>
            <svg
              width="128"
              height="128"
              viewBox="0 0 128 128"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M64 106.667H64.0533"
                stroke="black"
                strokeWidth="10.6667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M45.3333 87.6212C50.3181 82.7352 57.0199 79.9984 64 79.9984C70.9801 79.9984 77.6819 82.7352 82.6666 87.6212"
                stroke="black"
                strokeWidth="10.6667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M26.6667 68.5814C34.2295 61.1676 43.8283 56.1732 54.24 54.2347"
                stroke="black"
                strokeWidth="10.6667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M101.333 68.5813C98.1219 65.4333 94.5254 62.7041 90.6292 60.4586"
                stroke="black"
                strokeWidth="10.6667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.6667 47.04C17.2619 41.1417 24.7898 36.3784 32.944 32.944"
                stroke="black"
                strokeWidth="10.6667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M117.333 47.0398C109.198 39.7642 99.6609 34.23 89.3076 30.7776C78.9542 27.3253 68.004 26.0279 57.1306 26.9652"
                stroke="black"
                strokeWidth="10.6667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.6667 10.6667L117.333 117.333"
                stroke="black"
                strokeWidth="10.6667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className={classNames(styles.desc)}>
            <div className={classNames(styles.desc1)}>{i18n.t('common.offline.title')}</div>
            <div className={classNames(styles.desc2)}>{i18n.t('common.offline.desc')}</div>
          </div>
          <div className={classNames(styles.button)}>
            <OmButton className={classNames('w100', 'h38', 'type6')} onClick={result.onClickRetry}>
              {i18n.t('common.button.retry')}
            </OmButton>
          </div>
          <div className={classNames(styles.footer)}>
            <div className={styles.languageSwitcher}>
              <img src={LanguageIcon} alt="language" className="mr-2" />
              <LanguageSwitcher
                onChange={(): void => {
                  result.updateModal?.()
                }}
              />
            </div>
          </div>
        </div>
      </Provider>
    )
  }

  result.modalObj = confirmBase({
    wrapClassName: classNames(styles.offlineUI, opts.wrapClassName),
    closable: false,
    maskClosable: false,
    content: result.render(),
    width: null,
    footer: null,
  })

  result.updateModal = (): void => {
    result.modalObj?.updateBase?.({
      content: result.render!(),
    })
  }

  activeOfflineUI = result as OfflineUIResult
  return activeOfflineUI
}

const popupProps = {
  selectUser,
  scheduleSend,
  emlPreview,
  openSendMailInnerPopup,
  openSendMailWindow,
  openConfirmUI,
  checkOffline,
}

export default popupProps
